import { classNames } from '@progress/kendo-react-common'
import {
    DateHeaderCellProps,
    Scheduler,
    SchedulerDataChangeEvent,
    SchedulerEditSlot,
    SchedulerEditSlotProps,
    SchedulerSlotProps,
    SchedulerSlot,
    WeekView,
    SchedulerSlotHandle,
    SchedulerEditItem,
    SchedulerEditItemProps,
} from '@progress/kendo-react-scheduler'
import { useAuth } from 'context/AuthContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { StaffAvailabilityDto } from 'types/staff'
import { useFetch } from 'utils/fetch-helper'
import { getDateOfWeek, getWeek } from 'utils/scheduling-helper'
import { toHHMM12, toHHMM24, toYmd } from 'utils/string-localization-helper'
import moment from 'moment-timezone'
import { FetchMethods } from 'utils/types'
import { FormWithCustomEditor } from './CustomForm'
import { CustomItem } from './CustomItem'
import '@progress/kendo-date-math/tz/all'
import { ZonedDate } from '@progress/kendo-date-math'
import { useAppContext } from 'context/AppContext'
import {
    createStaffAvailability,
    expireStaffAvailability,
    updateStaffAvailability,
} from 'services/staff'
import { getTranslation } from '../../../../../utils/language-helper'
import { Fragment } from 'react'
import { Popup } from '@progress/kendo-react-popup'
import Translation from '../../../../Translation'
import { useTenantContext } from 'context/TenantContext'
import { AvailabilityApiStatus } from '..'
import ConfirmDialogModal from '../../../ConfirmDialog'
import { ServiceApiTypeEnum } from 'types/service'
import { useAppErrorApiContext } from 'context/AppErrorContext'

export type AvailabilityWeeklyViewProps = {
    setStatus(status: AvailabilityApiStatus): void
    disable: boolean
}

export default function AvailabilityWeeklyView({
    setStatus,
    disable,
}: AvailabilityWeeklyViewProps) {
    const { setGlobalError } = useAppErrorApiContext()
    const { translations } = useAppContext()
    const { StaffModal, user_profile, ServiceModal } = useAuth()
    const { services, locations } = useTenantContext()
    const { attributes } = StaffModal
    const [general_availability, setGeneralAvailability] = useState<any[]>(
        []
    )
    const [show_confirmation, setShowConfirmation] =
        useState<boolean>(false)
    const [appointments, setAppointments] = useState<any>(
        services.filter(
            (item) => item?.service_type === ServiceApiTypeEnum.APPOINTMENT
        )
    )

    const active_date = new Date()
    active_date.setHours(0, 0, 0, 0)

    const [dt_from, dt_to] = getWeek(0, active_date)
    const timezone = user_profile?.user?.timezone || 'Asia/Singapore'
    const local_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const { data: data_availability, doFetch: getAvailability } = useFetch(
        `/v1/staff/availability/general?staffId=${
            attributes?.id
        }&fromDate=${dt_from.toISOString().substring(0, 10)}&toDate=${dt_to
            .toISOString()
            .substring(0, 10)}`,
        FetchMethods.GET,
        !!attributes?.id
    )

    function mapAvailability(a: any) {
        const services = a.services
        let recurrence_days: string[] = []
        let dates: Date[] = []

        a.dayOfWeeks?.forEach((day: string) => {
            recurrence_days.push(day.substring(0, 2))
            dates.push(getDateOfWeek(day))
        })

        const sortedDate = dates.sort((a, b) => a.getTime() - b.getTime())
        const ymd_date = toYmd(sortedDate[0])

        const start_date = ZonedDate.fromLocalDate(
            new Date(
                moment
                    .tz(`${ymd_date} ${toHHMM24(a.fromTime)}`, timezone)
                    .format()
            ),
            timezone
        )
        const date = new Date(`${ymd_date}T${toHHMM24(a.fromTime)}:00`);
        if (isNaN(date.getTime())) {
            console.error("Invalid Date:", ymd_date, toHHMM24(a.fromTime));
        } else {
            const start_date = new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(date);
            console.log("Formatted Date:", start_date);
        }
        
        const end_date = ZonedDate.fromLocalDate(
            new Date(
                moment
                    .tz(`${ymd_date} ${toHHMM24(a.toTime)}`, timezone)
                    .format()
            ),
            timezone
        )

        return {
            ...a,
            services,
            start: start_date,
            end: end_date,
            recurrenceRule: `FREQ=WEEKLY;BYDAY=${recurrence_days.join()}`,
        }
    }

    useEffect(() => {
        if (
            data_availability &&
            data_availability.length >= 0 &&
            services &&
            services.length > 0
        ) {
            const today = new Date()
            const fetched_data: any[] = data_availability
                .filter(
                    (item: StaffAvailabilityDto) =>
                        !item.expiryDate ||
                        new Date(item.expiryDate as string).getTime() >
                            today.getTime()
                )
                .map(mapAvailability)
            setGeneralAvailability(fetched_data)
        }
    }, [data_availability, services])

    async function handleCreate(data: any) {
        let staff = {
            id: attributes?.id,
        }
        let location = data?.location ? data.location : null

        // const newDataSet = Array.from(new Set(data));

        // if (data.serviceIds.length != 0 && data.categoryIds.length != 0) {
        //     data.categoryIds.forEach((category: string) => {
        //         const svcIds = appointments.filter((svc: any) => svc?.category.id === category)
        //         svcIds.forEach((svc: any) => {
        //             data.serviceIds.push(svc.id)
        //         })
        //     })
        //     data.categoryIds = null
        // }

        let location_space_ids: string[] = []
        if (data.location_space) {
            location_space_ids = data.location_space
        }

        let availability: StaffAvailabilityDto = {
            staff: staff,
            block: data.block,
            service_group_id: '',
            serviceIds: data.serviceIds,
            categoryIds: data.categoryIds,
            location: location,
            dayOfWeeks: data.day_of_weeks,
            effectiveDate: '',
            expiryDate: '',
            fromTime: toHHMM12(data.from_time),
            toTime: toHHMM12(data.to_time),
        }

        if (location_space_ids.length != 0) {
            availability.locationSpaceIds = location_space_ids
        }

        setStatus(AvailabilityApiStatus.SAVING)
        let response = await createStaffAvailability(availability)
        if (response.id) {
            setStatus(AvailabilityApiStatus.SAVED_SUCCESS)
            getAvailability()
            if (
                ServiceModal.is_open &&
                !ServiceModal?.attributes?.refetch
            ) {
                ServiceModal.setAttributes({
                    ...ServiceModal.attributes,
                    refetch: true,
                })
            }
        } else {
            setStatus(AvailabilityApiStatus.SAVED_FAILURE)
            setGlobalError(response)
        }
    }

    async function handleUpdate(data: any) {
        let staff = {
            id: attributes?.id,
        }

        let location = data?.location ? data.location : null

        let svc_ids: string[] = []
        if (data.serviceIds?.length) {
            svc_ids = data.serviceIds
        }

        let category_ids: string[] = []
        if (data.categoryIds?.length) {
            category_ids = data.categoryIds
        }

        let location_space_ids: string[] = []
        if (data.location_space) {
            location_space_ids = data.location_space
        }

        let staff_availability_dto: StaffAvailabilityDto = {
            id: data.data_id,
            staff: staff,
            block: data.block,
            service_group_id: '',
            serviceIds: svc_ids,
            categoryIds: category_ids,
            location: location,
            dayOfWeeks: data.day_of_weeks,
            effectiveDate: '',
            expiryDate: '',
            fromTime: toHHMM12(data.from_time),
            toTime: toHHMM12(data.to_time),
        }

        if (location_space_ids.length != 0) {
            staff_availability_dto.locationSpaceIds = location_space_ids
        }

        setStatus(AvailabilityApiStatus.SAVING)
        let response = await updateStaffAvailability(staff_availability_dto)
        if (response.ok) {
            getAvailability()
            setStatus(AvailabilityApiStatus.SAVED_SUCCESS)
            if (
                ServiceModal.is_open &&
                !ServiceModal?.attributes?.refetch
            ) {
                ServiceModal.setAttributes({
                    ...ServiceModal.attributes,
                    refetch: true,
                })
            }
        } else {
            setStatus(AvailabilityApiStatus.SAVED_FAILURE)
            setGlobalError(response)
        }
    }

    async function handleDelete(id: string) {
        const staff_availability_dto = data_availability.filter(
            (i: StaffAvailabilityDto) => i.id == id
        )[0]
        let response = await expireStaffAvailability(
            staff_availability_dto,
            toYmd(new Date())
        )
        if (response.ok) {
            console.info('Successfully deleted')
            getAvailability()
            if (
                ServiceModal.is_open &&
                !ServiceModal?.attributes?.refetch
            ) {
                ServiceModal.setAttributes({
                    ...ServiceModal.attributes,
                    refetch: true,
                })
            }
        } else {
            console.info(response.error)
        }
    }

    function DateCell(p: DateHeaderCellProps) {
        return (
            <div
                {...p}
                className={classNames(
                    p.className || '',
                    'border-l-white px-1 border-r-none'
                )}
            >
                <div className="text-center w-full">
                    <div className="block py-2 mb-2">
                        {p.date.toLocaleDateString('default', {
                            weekday: 'short',
                        })}
                    </div>
                </div>
            </div>
        )
    }

    const CustomEditSlot = (props: SchedulerEditSlotProps) => {
        const [formItem, setFormItem] = useState(null)

        const handleFormItemChange = useCallback(
            (event) => {
                if (props.isWorkDay && props.isWorkHour) {
                    setFormItem(event.value)
                }
            },
            [props.isWorkDay, props.isWorkHour]
        )

        return (
            <SchedulerEditSlot
                {...props}
                formItem={formItem}
                className={disable ? 'cursor-not-allowed' : ''}
                onFormItemChange={handleFormItemChange}
                form={(p) => (
                    <FormWithCustomEditor
                        show={true}
                        data={p}
                        services={appointments}
                        locations={locations}
                        translations={translations}
                        onSave={async (staff_dto) => {
                            handleCreate(staff_dto)
                        }}
                        timezone={timezone}
                    />
                )}
            />
        )
    }

    const CustomEditItem = (props: SchedulerEditItemProps) => {
        const newProps = { ...props, showOccurrenceDialog: false }
        const [formItem, setFormItem] = useState(null)
        const [resizeItem, setResizeItem] = useState(null)
        const [dragItem, setDragItem] = useState(null)
        const [data, setDataItem] = useState(null)

        const handleFormItemChange = useCallback(
            (event) => {
                setFormItem(event.value)
            },
            [props.dataItem]
        )

        const handleResizeChange = (event: any) => {
            if (event.value) {
                const service_ids = event.value.services.map(
                    (item: any) => item.serviceId
                )
                const category_ids = event.value.categories?.map(
                    (item: any) => item.categoryId
                )
                const location = {
                    value: event.value.location.id,
                    text: event.value.location.name,
                }
                const data = {
                    block: event.value.block,
                    categoryIds: category_ids,
                    serviceIds: service_ids,
                    location: location,
                    dayOfWeeks: event.value.dayOfWeeks,
                    from_time: event.value.start,
                    to_time: event.value.end,
                }
                //setDataItem(data)
                setShowConfirmation(true)
            }
        }

        const handleDragChange = (event: any) => {
            if (!event.value) {
                const service_ids = event.value.services.map(
                    (item: any) => item.serviceId
                )
                const category_ids = event.value.categories?.map(
                    (item: any) => item.categoryId
                )
                const location = {
                    value: event.value.location.id,
                    text: event.value.location.name,
                }
                const data = {
                    block: event.value.block,
                    categoryIds: category_ids,
                    serviceIds: service_ids,
                    location: location,
                    dayOfWeeks: event.value.dayOfWeeks,
                    from_time: event.value.start,
                    to_time: event.value.end,
                }
                // setShowConfirmation(true)
            }
        }

        return (
            <Fragment>
                <SchedulerEditItem
                    {...newProps}
                    formItem={formItem}
                    onFormItemChange={handleFormItemChange}
                    onResizeItemChange={handleResizeChange}
                    form={(p) => (
                        <FormWithCustomEditor
                            show={true}
                            data={p}
                            services={appointments}
                            locations={locations}
                            translations={translations}
                            onSave={async (staff_dto) => {
                                handleUpdate(staff_dto)
                            }}
                            onDelete={async (id: string) => {
                                handleDelete(id)
                            }}
                            timezone={local_timezone}
                        />
                    )}
                />
                {show_confirmation && (
                    <ConfirmDialogModal
                        prompt_message={getTranslation(
                            'delete_availability_prompt',
                            translations
                        )}
                        onCancel={() => {
                            setShowConfirmation(false)
                        }}
                        onConfirm={() => handleUpdate(data)}
                    />
                )}
            </Fragment>
        )
    }

    const CustomSlot = (props: SchedulerSlotProps) => {
        const ref = useRef<SchedulerSlotHandle>(null)
        const [show, setShow] = useState(false)
        const [focus, setFocus] = useState(false)

        return (
            <Fragment>
                <SchedulerSlot
                    {...props}
                    ref={ref}
                    style={{
                        ...props.style,
                        borderColor:
                            props.isAllDay ||
                            !props.isWorkHour ||
                            !props.isWorkDay
                                ? '#000000'
                                : '#ffffff',
                        backgroundColor:
                            (show || focus) && !disable ? '#d2c7fc' : '',
                        cursor: show ? 'pointer' : '',
                    }}
                    onFocus={() => {
                        setFocus(true)
                    }}
                    onBlur={() => {
                        setFocus(false)
                    }}
                    onMouseOver={() => {
                        setShow(true)
                    }}
                    onMouseOut={() => {
                        setShow(false)
                    }}
                />
                <Popup
                    show={show && !disable}
                    animate={false}
                    anchorAlign={{
                        horizontal: 'center',
                        vertical: 'center',
                    }}
                    popupAlign={{
                        horizontal: 'center',
                        vertical: 'bottom',
                    }}
                    anchor={ref.current && ref.current.element}
                    popupClass={
                        'bg-black text-white text-xs shadow-lg rounded-xl w-auto px-2 py-1 mt-2'
                    }
                    className="z-[102]"
                >
                    <div className="rounded" style={{ overflow: 'hidden' }}>
                        <Translation
                            render_as="span"
                            content_key="new_availability"
                        />
                    </div>
                </Popup>
            </Fragment>
        )
    }

    return (
        <div style={{ height: '640px' }}>
            <Scheduler
                timezone={local_timezone}
                data={general_availability}
                item={(props) => (
                    <CustomItem
                        {...props}
                        onDelete={async (id: string) => {
                            handleDelete(id)
                        }}
                    />
                )}
                onDataChange={({
                    created,
                    updated,
                    deleted,
                }: SchedulerDataChangeEvent) => {
                    if (created.length) {
                        handleCreate(created)
                    }
                    if (updated.length) {
                        handleUpdate(updated[0])
                    }
                    if (deleted.length) {
                        handleDelete(deleted[0])
                    }
                }}
                className="h-full"
                view={'week'}
                header={() => <></>}
                footer={() => <></>}
                editable={{
                    add: !disable ? true : false,
                    remove: false,
                    drag: false,
                    resize: false,
                    edit: true,
                    select: false,
                }}
                editSlot={CustomEditSlot}
                // viewItem={SchedulerProportionalViewItem}
            >
                <WeekView
                    slotDivisions={1}
                    slot={CustomSlot}
                    slotDuration={60}
                    dateHeaderCell={DateCell}
                    workDayStart={'00:00'}
                    workDayEnd={'23:59'}
                    workWeekStart={-1}
                    workWeekEnd={7}
                    currentTimeMarker={false}
                    editItem={CustomEditItem} //TODO: for custom edit item slot
                />
            </Scheduler>
        </div>
    )
}
